import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import AlertsPane from "@/components/alerts/AlertsPane";
import { Bell } from "lucide-react";
import { alertsApi } from "@/lib/api/alerts";
import { useUserContext } from "@/contexts/UserContext";

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';

export default function AlertBellTrigger() {
  const { id: userId, currentTenant: tenantId } = useUserContext();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasUnseen, setHasUnseen] = useState(false);

  const loadCounts = useCallback(async () => {
    if (!userId || !tenantId) return;
    try {
      const alerts = await alertsApi.list(userId, tenantId, 100);

      const unread = alerts.filter(a => !a.read_at);

      // Load outbox count
      let outboxCount = 0;
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/outbox/new-count?tenant_id=${tenantId}&user_id=${userId}`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          outboxCount = data.count || 0;
        }
      } catch (e) {
        console.warn('Could not fetch outbox count:', e);
      }

      const actionableUnreadCount = unread.length + outboxCount;
      setUnreadCount(actionableUnreadCount);
      // Keep the red-dot fallback aligned with actionable items only.
      // If there is nothing to mark as read, there should be no flag.
      setHasUnseen(actionableUnreadCount > 0);
    } catch (error) {
      // Degrade gracefully - avoid spamming console
      if (process.env.NODE_ENV === 'development') {
        console.warn('AlertBellTrigger: Could not load counts', error?.message);
      }
    }
  }, [userId, tenantId]);

  useEffect(() => {
    loadCounts();

    const handler = () => setTimeout(loadCounts, 800);
    window.addEventListener('alerts-updated', handler);
    window.addEventListener('outbox-updated', handler);
    return () => {
      window.removeEventListener('alerts-updated', handler);
      window.removeEventListener('outbox-updated', handler);
    };
  }, [loadCounts]);

  useEffect(() => {
    if (!userId) return;
    const id = setInterval(loadCounts, 60_000);
    return () => clearInterval(id);
  }, [loadCounts, userId]);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    // Give a longer delay to ensure DB updates have propagated
    setTimeout(loadCounts, 800);
  };

  const handleAlertsSeen = () => {
    setHasUnseen(false);
    // Refresh counts after a delay
    setTimeout(loadCounts, 800);
  };

  const handleAlertsRead = () => {
    setTimeout(loadCounts, 800);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleOpen}
        className="relative text-gray-400 hover:text-white hover:bg-gray-800/50"
        aria-label="Open alerts"
      >
        <Bell className="w-5 h-5" />

        {/* Numeric badge for unread alerts */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-500 text-white min-w-[18px]">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}

        {/* Fallback dot if for some reason there are unseen alerts but no unread */}
        {unreadCount === 0 && hasUnseen && (
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500" />
        )}
      </Button>

      <AlertsPane
        isOpen={isOpen}
        onClose={handleClose}
        onAlertsSeen={handleAlertsSeen}
        onAlertsRead={handleAlertsRead}
      />
    </>
  );
}